import { useMemo } from 'react';
import { useAuth } from './AuthContext';
import { permits, type PermissionScope } from './permissionScope';

const FRANCHISE_WIDE: PermissionScope = { kind: 'franchise_wide' };

/**
 * The single frontend authorizer. One `useAuthorization()` call yields an object whose
 * methods are plain functions (not hooks), so a component may ask as many permission
 * questions as it likes — in loops, conditionals, or `.filter` callbacks — without
 * tripping React's rules of hooks. That is what lets the shells gate their nav inline
 * instead of pre-resolving every code up front.
 *
 * Both checks apply the shared `permits` rule (ADR 0007):
 *  - a franchise-wide grant holds at every venue and franchise-wide;
 *  - a venue-scoped grant holds only at that venue;
 *  - omitting the scope asks the franchise-wide question.
 *
 * This gates UI controls only. The backend 403 stays authoritative — never rely on the
 * authorizer for actual access control.
 *
 * @example
 *   const auth = useAuthorization();
 *   const canManageUsers = auth.can('iam.user.manage');
 *   const canSeeHere = auth.can('venue.view', { kind: 'venue', venueId });
 *   const showFranchiseNav = auth.canAny(['franchise.brand.manage', 'franchise.country.manage']);
 */
export type Authorizer = {
  /** Whether the current user holds `code` at `scope` (defaults to franchise-wide). */
  can(code: string, scope?: PermissionScope): boolean;
  /** Whether the current user holds any of `codes` at `scope` (defaults to franchise-wide). */
  canAny(codes: string[], scope?: PermissionScope): boolean;
};

export function useAuthorization(): Authorizer {
  const { permissions } = useAuth();
  return useMemo<Authorizer>(() => {
    const can = (code: string, scope: PermissionScope = FRANCHISE_WIDE) =>
      permits(permissions, code, scope);
    return {
      can,
      canAny: (codes, scope) => codes.some((code) => can(code, scope)),
    };
  }, [permissions]);
}
